<template>
  <div>
    <div class="task-scheduler-title">
      <div>{{ $t('task_scheduler.title') }}</div>
    </div>

    <!-- 顶部统计 -->
    <Row :gutter="12" style="margin-bottom: 12px;">
      <Col span="6">
        <Card class="stat-card stat-all" @click.native="filterByStatus(null)">
          <div class="stat-label">{{ $t('task_scheduler.allTasks') }}</div>
          <div class="stat-value">{{ statistics.total }}</div>
        </Card>
      </Col>
      <Col span="6">
        <Card class="stat-card stat-pending" @click.native="filterByStatus(0)">
          <div class="stat-label">{{ $t('task_scheduler.pending') }}</div>
          <div class="stat-value">{{ statistics.pending }}</div>
        </Card>
      </Col>
      <Col span="6">
        <Card class="stat-card stat-running" @click.native="filterByStatus(1)">
          <div class="stat-label">{{ $t('task_scheduler.executing') }}</div>
          <div class="stat-value">{{ statistics.executing }}</div>
        </Card>
      </Col>
      <Col span="6">
        <Card class="stat-card stat-completed" @click.native="filterByStatus(2)">
          <div class="stat-label">{{ $t('task_scheduler.completed') }}</div>
          <div class="stat-value">{{ statistics.completed }}</div>
        </Card>
      </Col>
    </Row>

    <!-- 搜索区 -->
    <Row :gutter="8" style="margin-bottom: 8px;">
      <Col span="5">
        <Input v-model="searchForm.keyword" clearable :placeholder="$t('task_scheduler.searchPlaceholder')"></Input>
      </Col>
      <Col span="4" v-if="companyIdCookie==1">
        <Select v-model="searchForm.companyId" filterable clearable :placeholder="$t('task_scheduler.customer')" :transfer="true">
          <Option :value="item.id" v-for="item in companyList" :key="item.id">{{ item.name }}</Option>
        </Select>
      </Col>
      <Col span="4">
        <Select v-model="searchForm.accountId" filterable clearable :placeholder="$t('task_scheduler.account')" :transfer="true">
          <Option :value="item.id" v-for="item in accountList" :key="item.id">{{ item.name }}</Option>
        </Select>
      </Col>
      <Col span="4">
        <Select v-model="searchForm.status" clearable :placeholder="$t('task_scheduler.taskStatus')" :transfer="true">
          <Option v-for="item in statusOptions" :value="item.value" :key="item.value">{{ item.label }}</Option>
        </Select>
      </Col>
      <Col span="5">
        <DatePicker
          type="daterange"
          format="yyyy-MM-dd"
          v-model="searchForm.planTimeRange"
          :placeholder="$t('task_scheduler.plannedTime')"
          :transfer="true"
          @on-change="onPlanTimeChange"
          style="width: 100%;">
        </DatePicker>
      </Col>
      <Col span="2">
        <Button type="primary" @click="loadTasks">{{ $t('task_scheduler.search') }}</Button>
      </Col>
    </Row>

    <!-- 任务列表 -->
    <Table
      :no-data-text="$t('common.noData')"
      :columns="columns"
      :data="tableData"
      :loading="tableLoading"
      style="width: 100%;">
      <template slot-scope="{ row }" slot="status">
        <Tag :color="statusColor(row.status)">{{ statusText(row.status) }}</Tag>
      </template>
      <template slot-scope="{ row }" slot="operation">
        <Button type="text" size="small" style="color:#2d8cf0;" @click="viewTaskDetail(row)">{{ $t('task_scheduler.viewDetail') }}</Button>
        <Button v-if="canCancel(row.status)" type="text" size="small" style="color:#ed4014;" @click="confirmCancel(row)">{{ $t('task_scheduler.cancel') }}</Button>
      </template>
    </Table>

    <Page
      :current="current"
      :total="total"
      show-sizer
      show-total
      :page-size="pageSize"
      @on-change="changePage"
      @on-page-size-change="changeSize"
      :transfer="true"
      style="margin-top: 12px; text-align: right;">
    </Page>
  </div>
</template>

<script>
import { getCookie } from '../../../utils/cookieUtil.js';

// 任务状态枚举（按对接文档 1.5）
// 0-待执行 1-执行中 2-已完成 3-执行失败 4-已取消
var STATUS_PENDING = 0;
var STATUS_EXECUTING = 1;
var STATUS_COMPLETED = 2;
var STATUS_FAILED = 3;
var STATUS_CANCELED = 4;

export default {
  name: 'TaskScheduler',
  data() {
    return {
      companyIdCookie: getCookie(global.companyId),
      tableLoading: false,
      current: 1,
      pageSize: 10,
      total: 0,
      statistics: {
        total: 0,
        pending: 0,
        executing: 0,
        completed: 0
      },
      searchForm: {
        keyword: '',
        companyId: '',
        accountId: '',
        status: null,
        planTimeRange: ['', ''],
        plannedStartTime: '',
        plannedEndTime: ''
      },
      companyList: [],
      accountList: [],
      tableData: []
    };
  },
  computed: {
    statusOptions() {
      return [
        { value: STATUS_PENDING, label: this.$t('task_scheduler.pending') },
        { value: STATUS_EXECUTING, label: this.$t('task_scheduler.executing') },
        { value: STATUS_COMPLETED, label: this.$t('task_scheduler.completed') },
        { value: STATUS_FAILED, label: this.$t('task_scheduler.failed') },
        { value: STATUS_CANCELED, label: this.$t('task_scheduler.canceled') }
      ];
    },
    columns() {
      var vm = this;
      return [
        { title: this.$t('task_scheduler.taskNumber'), key: 'taskNumber', minWidth: 160 },
        { title: this.$t('task_scheduler.taskName'), key: 'taskName', minWidth: 220 },
        { title: this.$t('task_scheduler.customer'), key: 'companyName', minWidth: 140 },
        {
          title: this.$t('task_scheduler.account'),
          key: 'accountName',
          minWidth: 180,
          render: (h, { row }) => {
            if (row.accountGroupId) {
              return h('div', vm.$t('task_scheduler.accountGroup') + (row.accountGroupName || ''));
            }
            if (row.accountId) {
              return h('div', vm.$t('task_scheduler.accountPrefix') + (row.accountName || ''));
            }
            return h('span', '-');
          }
        },
        { title: this.$t('task_scheduler.plannedTime'), key: 'plannedExecutionTime', minWidth: 160 },
        { title: this.$t('task_scheduler.taskStatus'), slot: 'status', minWidth: 110 },
        {
          title: this.$t('task_scheduler.relatedTicket'),
          key: 'ticketNumber',
          minWidth: 160,
          render: (h, { row }) => {
            if (!row.ticketNumber) return h('span', '-');
            return h('a', {
              style: { color: '#2d8cf0' },
              on: {
                click: () => vm.toTicketDetail(row)
              }
            }, row.ticketNumber);
          }
        },
        { title: this.$t('task_scheduler.operation'), slot: 'operation', fixed: 'right', minWidth: 160 }
      ];
    }
  },
  mounted() {
    if (this.companyIdCookie == 1) {
      this.loadCompanyList();
    } else {
      this.searchForm.companyId = parseInt(this.companyIdCookie);
    }
    this.loadStatistics();
    this.loadTasks();
  },
  methods: {
    loadCompanyList() {
      // 沿用全局公司下拉接口
      this.$api.company.getCompanyDropDownBox('userName', getCookie(global.cookieName))
        .then(res => {
          this.companyList = res.data || [];
        })
        .catch(() => {
          this.companyList = [];
        });
    },
    loadAccountList(companyId) {
      if (!companyId) {
        this.accountList = [];
        return;
      }
      this.$api.account.listAccountsByCompanyIdAndCloudPlatformType({ companyId: companyId })
        .then(res => {
          this.accountList = res.data || [];
        })
        .catch(() => {
          this.accountList = [];
        });
    },
    loadStatistics() {
      // 文档 1.1：GET /sme/agentverse/task/statistics（无入参）
      this.$api.taskScheduler.getTaskStatistics().then(res => {
        var d = (res.data && res.data.data) || res.data || {};
        this.statistics = {
          total: d.total || 0,
          pending: d.pending || 0,
          executing: d.executing || 0,
          completed: d.completed || 0
        };
      }).catch(() => {});
    },
    buildQuery() {
      // 状态字段允许 0，使用显式判空避免 0 被当成 null
      var status = this.searchForm.status;
      if (status === '' || status === undefined) status = null;
      return {
        keyword: this.searchForm.keyword || null,
        companyId: this.searchForm.companyId || null,
        accountId: this.searchForm.accountId || null,
        status: status,
        plannedStartTime: this.searchForm.plannedStartTime || null,
        plannedEndTime: this.searchForm.plannedEndTime || null
      };
    },
    loadTasks() {
      this.tableLoading = true;
      var params = Object.assign({}, this.buildQuery(), {
        pageNum: this.current,
        pageSize: this.pageSize
      });
      this.$api.taskScheduler.pageTaskList(params).then(res => {
        // 兼容 { data: { records } } 与直接 { records } 两种结构
        var d = (res.data && res.data.data) || res.data || {};
        if (d.records) {
          this.tableData = d.records;
          this.total = d.total || 0;
          this.current = d.current || d.pageNum || 1;
        } else if (Array.isArray(d)) {
          this.tableData = d;
          this.total = d.length;
        } else {
          this.tableData = [];
          this.total = 0;
        }
      }).catch(() => {
        this.tableData = [];
        this.total = 0;
      }).finally(() => {
        this.tableLoading = false;
      });
      this.loadStatistics();
    },
    onPlanTimeChange(range) {
      if (Array.isArray(range)) {
        this.searchForm.plannedStartTime = range[0] || '';
        this.searchForm.plannedEndTime = range[1] || '';
      } else {
        this.searchForm.plannedStartTime = '';
        this.searchForm.plannedEndTime = '';
      }
    },
    filterByStatus(status) {
      this.searchForm.status = status;
      this.current = 1;
      this.loadTasks();
    },
    changePage(p) {
      this.current = p;
      this.loadTasks();
    },
    changeSize(s) {
      this.pageSize = s;
      this.current = 1;
      this.loadTasks();
    },
    statusText(status) {
      var item = this.statusOptions.find(it => it.value === status);
      return item ? item.label : '-';
    },
    statusColor(status) {
      switch (status) {
        case STATUS_PENDING: return 'default';
        case STATUS_EXECUTING: return 'blue';
        case STATUS_COMPLETED: return 'green';
        case STATUS_FAILED: return 'red';
        case STATUS_CANCELED: return 'default';
        default: return 'default';
      }
    },
    canCancel(status) {
      // 文档 1.4：只有待执行和执行中的任务可取消
      return status === STATUS_PENDING || status === STATUS_EXECUTING;
    },
    viewTaskDetail(row) {
      this.$router.push({
        path: '/ticketCenter/taskScheduler/detail',
        query: { taskNumber: row.taskNumber }
      });
    },
    toTicketDetail(row) {
      if (!row.ticketId) return;
      this.$router.push({
        path: '/cases/caseDetail',
        query: { id: row.ticketId }
      });
    },
    confirmCancel(row) {
      this.$Modal.confirm({
        title: this.$t('task_scheduler.cancelTask'),
        content: this.$t('task_scheduler.cancelConfirm'),
        onOk: () => {
          this.$api.taskScheduler.cancelTask(row.taskNumber).then(res => {
            var data = res.data || {};
            if (data.code != null && data.code !== 200) {
              this.$Message.error(data.message || this.$t('task_scheduler.cancelFailed'));
              return;
            }
            this.$Message.success(this.$t('task_scheduler.canceled'));
            this.loadTasks();
          });
        }
      });
    }
  },
  watch: {
    'searchForm.companyId'(val) {
      this.searchForm.accountId = '';
      if (val) {
        this.loadAccountList(val);
      } else {
        this.accountList = [];
      }
    }
  }
};
</script>

<style scoped>
.task-scheduler-title {
  width: 100%;
  font-size: 13px;
  background-color: #fff;
  box-sizing: border-box;
  padding: 15px 0px 8px 4px;
  font-weight: 600;
}
.stat-card {
  cursor: pointer;
  text-align: center;
  transition: box-shadow 0.2s;
}
.stat-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.stat-label {
  font-size: 13px;
  color: #515a6e;
}
.stat-value {
  font-size: 24px;
  font-weight: 600;
  margin-top: 4px;
}
.stat-all .stat-value { color: #515a6e; }
.stat-pending .stat-value { color: #2d8cf0; }
.stat-running .stat-value { color: #ff9900; }
.stat-completed .stat-value { color: #19be6b; }
</style>



// WEBPACK FOOTER //
// src/components/cases/task_scheduler/TaskScheduler.vue